import { User, UserRole } from '../types/User';

// Every staff-only UI gate, one predicate per gate. The gates hold different
// people on purpose: do not merge them, and add a new predicate rather than
// widening one. They only shape the UI (a clean redirect, a hidden tab or
// field); ora-be enforces access. ESLint (`no-restricted-syntax` in
// eslint.config.mjs) rejects staff email literals anywhere else in src/.

type UserWithEmail = Pick<User, 'email'> | null | undefined;

/**
 * Emails allowed to access the admin dashboard, in addition to holding the
 * `admin` role. The real enforcement lives on the backend in ora-be
 * src/helpers/authorizationHelpers.ts (ADMIN_DASHBOARD_EMAILS). Keep the two
 * lists in sync.
 */
const ADMIN_DASHBOARD_EMAILS = new Set([
  'jacob@oraai.com',
  'kevin@oraai.com',
  'ryan@oraai.com',
]);

/** The admin dashboard (`AdminRoute`) and the admin-only controls elsewhere. */
export const canAccessAdminDashboard = (
  user: Pick<User, 'role' | 'email'> | null | undefined
): boolean => {
  if (!user || user.role !== UserRole.admin) {
    return false;
  }

  const email = user.email?.trim().toLowerCase();
  return !!email && ADMIN_DASHBOARD_EMAILS.has(email);
};

/** Super admins, by user id. */
const SUPER_ADMINS = {
  'b6f352de-bfba-44b5-b92c-879ff8f2aa99': {
    email: 'kevin@oraai.com',
  },
  '971fcbef-c5ec-4bf4-a3a1-c10222c2a7f4': {
    email: 'jacob@oraai.com',
  },
};

/**
 * Super admins: the instances editor, Notifications and Love in the admin
 * dashboard, webinar management, group-survey respondents, cohort editing and
 * topic icons.
 */
export const isSuperAdmin = (
  userOrId: string | User | null | undefined
): boolean => {
  if (!userOrId) return false;

  let userId: string;

  if (typeof userOrId === 'string') {
    userId = userOrId;
  } else {
    userId = userOrId.id;
  }

  return Object.keys(SUPER_ADMINS).includes(userId);
};

/** The Ambassadors tab of the admin dashboard: Jacob and Ryan. */
export const canViewAmbassadorsTab = (user: UserWithEmail): boolean =>
  user?.email === 'jacob@oraai.com' || user?.email === 'ryan@oraai.com';

/** "Modify Payment" on an advocate in the Advocate Dashboard: Jacob and Kevin. */
export const canModifyAdvocatePayment = (user: UserWithEmail): boolean =>
  user?.email === 'jacob@oraai.com' || user?.email === 'kevin@oraai.com';
